import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { a34, a34Mq } from '@/components/Auction34/tokens';
import { m } from '@/components/Mansion/tokens';

/* ==========================================================================
   로그인 화면 (PORTAL-13709)

   두 톤이 한 파일에 있습니다. 화면 구조(중앙 한 덩어리 + CTA 하나)가 같고
   토큰만 갈리는 정도라, 폴더를 나누면 같은 레이아웃을 두 벌 관리하게 됩니다.

   Auction34 는 `--c-*`, Mansion 은 `--m-*` 으로 변수 접두어가 달라 두
   GlobalStyles 가 함께 주입되어도 서로 덮어쓰지 않습니다.
   ========================================================================== */

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;

/* --- 공통 골격 ------------------------------------------------------------ */

/**
 * 화면 전체를 채우는 중앙 정렬 틀.
 * 100dvh 는 모바일 브라우저 주소창이 접힐 때 생기는 점프를 막습니다.
 * 공통 Layout(min-height 1024px)을 쓰지 않는 이유이기도 합니다 — 그 값이면
 * 모바일에서 로그인 버튼 하나짜리 화면에 세로 스크롤이 생깁니다.
 */
export const Stage = styled.main`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 40px 20px;
  overflow: hidden;
`;

/** 중앙 콘텐츠. 등장 애니메이션은 여기 한 번만 겁니다. */
export const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${rise} 520ms cubic-bezier(0.22, 0.61, 0.36, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* --- 맨션상점(옥션) 톤 ----------------------------------------------------- */

export const AuctionStage = styled(Stage)`
  background: ${a34.color.bg};
  color: ${a34.color.text};
  font-family: ${a34.font.kr};
`;

/**
 * 체인에 매달린 간판(624×1271). 이미지 안에 "34 MANSION / 오늘만 열리는
 * 맨션 상점" 문구가 이미 들어 있어 이 화면의 제목 역할을 합니다.
 * 그래서 아래 카드에는 안내 한 줄과 CTA 만 둡니다.
 *
 * 위쪽 체인이 화면 밖에서 내려오는 형태라 Stage 상단에 붙여 매답니다.
 * 세로로 긴 그림이라 높이(vh)까지 함께 걸어 낮은 창에서 카드를 밀어내지
 * 않게 합니다.
 */
export const AuctionSign = styled.div`
  width: clamp(132px, 20vw, 190px);
  max-height: 46vh;
  /* Stage 의 상단 패딩만큼 끌어올려 체인이 화면 끝에서 시작하게 합니다. */
  margin-top: -40px;
  margin-bottom: 20px;

  img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 46vh;
    /* 높이에 걸리면 폭을 줄여 비율을 지킵니다. */
    object-fit: contain;
  }

  ${a34Mq.mobile} {
    width: clamp(120px, 38vw, 160px);
    max-height: 36vh;

    img {
      max-height: 36vh;
    }
  }
`;

export const AuctionCard = styled.div`
  width: 100%;
  padding: 28px 26px 26px;
  background: ${a34.color.surface};
  border: 1px solid ${a34.line.warm};
  border-radius: ${a34.radius.panel};
  box-shadow: ${a34.shadow.panel};
`;

export const AuctionKicker = styled.p`
  margin: 0 0 8px;
  font-family: ${a34.font.en};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  color: ${a34.color.goldSoft};
`;

/**
 * 화면의 h1 은 간판 이미지가 아니라 이 문장입니다.
 * 간판은 그림이라 확대·이미지 차단 환경에서 제목이 사라지면 안 됩니다.
 */
export const AuctionTitle = styled.h1`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.5;
  color: ${a34.color.purpleDark};
  word-break: keep-all;
`;

export const AuctionText = styled.p`
  margin: 10px 0 0;
  font-size: 13.5px;
  line-height: 1.7;
  color: ${a34.color.textSub};
  word-break: keep-all;
`;

/* --- 34 MANSION(하우스 안내) 톤 -------------------------------------------- */

export const MansionStage = styled(Stage)`
  background: ${m.surface.bg};
  color: ${m.ink.text};
  font-family: ${m.type.sans};
`;

export const MansionFrame = styled.div`
  width: 100%;
  padding: 44px 28px 36px;
  background: ${m.surface.surface};
  border: 1px solid ${m.surface.border};
`;

export const MansionLabel = styled.p`
  margin: 0 0 18px;
  font-size: ${m.type.label};
  font-weight: 500;
  letter-spacing: 0.24em;
  color: ${m.ink.textMuted};
`;

/**
 * 세리프 워드마크. 하우스 안내의 타이틀과 같은 글꼴·자간입니다.
 * 이미지가 아니라 텍스트라 이 화면은 외부 에셋 요청이 없습니다.
 */
export const MansionTitle = styled.h1`
  margin: 0;
  font-family: ${m.type.serif};
  font-size: clamp(34px, 8vw, 46px);
  font-weight: 500;
  line-height: 1.1;
  letter-spacing: 0.06em;
  color: ${m.ink.navy};
`;

/** 타이틀 아래 금선. 하우스 안내에서 제목을 받치는 표식과 같은 역할입니다. */
export const MansionRule = styled.div`
  width: 56px;
  height: 1px;
  margin: 20px auto 0;
  background: ${m.accent.gold};
`;

export const MansionText = styled.p`
  margin: 20px 0 0;
  font-size: ${m.type.body};
  line-height: 1.8;
  color: ${m.ink.textBody};
  word-break: keep-all;
  text-wrap: pretty;
`;

/* --- MS 로그인 버튼 -------------------------------------------------------- */

/**
 * 두 톤이 색만 다르고 형태가 같아 한 컴포넌트에 CSS 변수로 뺐습니다.
 *
 * 기존 화면은 글자·테두리가 모두 #B7B7C4 였고 배경이 #F7F7F7 이라 대비가
 * 1.9:1 이었습니다(WCAG AA 4.5:1). 두 톤 모두 채움 버튼에 흰 글자로 바꿔
 * 대비를 확보했습니다.
 */
export const MsButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  /* 손가락으로 누르는 영역이라 44px 아래로 내려가지 않게 합니다. */
  min-height: 52px;
  padding: 0 20px;
  border-radius: var(--login-cta-radius);
  background: var(--login-cta-bg);
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  transition: background 160ms ease, transform 160ms ease;

  /* 아이콘 배경이 흰색이라 채움 버튼 위에서 각지게 떠 보이는 것을 눌러 줍니다. */
  img {
    border-radius: 2px;
  }

  :hover {
    background: var(--login-cta-bg-hover);
  }

  :active {
    transform: translateY(1px);
  }

  :focus-visible {
    outline: 2px solid var(--login-cta-bg);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** 버튼 자리를 차지하는 로딩 표시. 실제 버튼과 같은 높이라 화면이 튀지 않습니다. */
export const MsButtonSkeleton = styled.div`
  width: 100%;
  min-height: 52px;
  border-radius: var(--login-cta-radius);
  background: var(--login-skeleton-bg);
  animation: ${keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: .55; }
  `} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * 로그인 URL 을 못 받았을 때.
 * 예전에는 이 경우 스켈레톤이 영원히 돌아서 사용자가 기다릴지 새로고침할지
 * 판단할 수 없었습니다.
 */
export const ErrorBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

export const ErrorText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--login-error-fg);
  word-break: keep-all;
`;

export const RetryButton = styled.button`
  min-height: 44px;
  padding: 0 22px;
  border: 1px solid var(--login-cta-bg);
  border-radius: var(--login-cta-radius);
  background: transparent;
  color: var(--login-cta-bg);
  font-size: 14px;
  font-weight: 600;

  :focus-visible {
    outline: 2px solid var(--login-cta-bg);
    outline-offset: 3px;
  }
`;

/** CTA 를 감싸는 여백. 카드 안쪽 아래에 붙습니다. */
export const Action = styled.div`
  margin-top: 26px;
  width: 100%;
`;

/* --- 톤별 CTA 변수 --------------------------------------------------------- */

export const auctionCtaVars = {
  '--login-cta-bg': a34.color.purple,
  '--login-cta-bg-hover': a34.color.purpleDark,
  '--login-cta-radius': a34.radius.pill,
  '--login-skeleton-bg': a34.color.surfaceLilac,
  '--login-error-fg': a34.color.dangerFg,
} as const;

export const mansionCtaVars = {
  '--login-cta-bg': m.accent.purple,
  '--login-cta-bg-hover': m.ink.navy,
  /* 34 MANSION 은 시스템 전면 radius 0 이라 버튼도 각지게 둡니다. */
  '--login-cta-radius': '0px',
  '--login-skeleton-bg': m.surface.surfaceAlt,
  '--login-error-fg': m.ink.textBody,
} as const;
